/**
 * Panel lateral del asistente IA (Fase 1, sólo lectura).
 *
 * El botón del topbar sólo aparece si el usuario tiene `asistente.usar` y
 * el comité tiene el asistente encendido (tope > 0 en la Console). El panel
 * muestra el uso del día y del mes: son los límites que evitan que el cupo
 * se gaste de más (ver docs/DISENO_ASISTENTE_IA.md §4).
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { History, Loader2, Plus, SendHorizonal, Sparkles } from "lucide-react";

import { Drawer } from "../../components/Drawer";
import { Button, Cargando, ErrorCarga } from "../../components/ui";
import { ApiError } from "../../lib/api";
import {
  ETIQUETA_HERRAMIENTA,
  fetchConversaciones,
  fetchEstado,
  fetchMensajes,
  preguntar,
  type Mensaje,
} from "./api";

const EJEMPLOS = [
  "¿Cuánto debe el socio Juan Pérez?",
  "¿Qué medidores marcaron cero este mes?",
  "¿Cómo vamos con la recaudación del mes pasado?",
  "¿Qué reclamos están por vencer?",
];

export function AsistenteBoton(): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const estado = useQuery({ queryKey: ["asistente", "estado"], queryFn: fetchEstado });

  if (!estado.data?.habilitado) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-md bg-brand-100 px-3 py-1.5 text-sm font-medium text-brand-800 hover:bg-brand-200 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        title="Preguntarle al asistente"
        aria-label="Asistente"
      >
        <Sparkles className="h-4 w-4" aria-hidden="true" />
        <span className="hidden sm:inline">Asistente</span>
      </button>
      <AsistentePanel open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function AsistentePanel({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [conversacionId, setConversacionId] = useState<number | null>(null);
  const [verHistorial, setVerHistorial] = useState(false);
  const [texto, setTexto] = useState("");
  // Pregunta enviada que todavía no vuelve del servidor.
  const [pendiente, setPendiente] = useState<string | null>(null);
  const finRef = useRef<HTMLDivElement>(null);

  const estado = useQuery({
    queryKey: ["asistente", "estado"],
    queryFn: fetchEstado,
    enabled: open,
  });
  const conversaciones = useQuery({
    queryKey: ["asistente", "conversaciones"],
    queryFn: fetchConversaciones,
    enabled: open && verHistorial,
  });
  const mensajes = useQuery({
    queryKey: ["asistente", "mensajes", conversacionId],
    queryFn: () => fetchMensajes(conversacionId as number),
    enabled: open && conversacionId !== null,
  });

  const mut = useMutation({
    mutationFn: (t: string) => preguntar(t, conversacionId),
    onMutate: (t) => {
      setPendiente(t);
      setTexto("");
    },
    // Si falla, la pregunta vuelve al cuadro de texto para reintentar.
    onError: (_err, t) => setTexto(t),
    onSuccess: (r) => {
      setConversacionId(r.conversacion_id);
      void qc.invalidateQueries({ queryKey: ["asistente", "mensajes", r.conversacion_id] });
      void qc.invalidateQueries({ queryKey: ["asistente", "conversaciones"] });
    },
    onSettled: () => {
      setPendiente(null);
      void qc.invalidateQueries({ queryKey: ["asistente", "estado"] });
    },
  });

  useEffect(() => {
    finRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [mensajes.data, pendiente]);

  const enviar = (t: string): void => {
    const limpio = t.trim();
    if (limpio.length < 2 || mut.isPending) return;
    mut.mutate(limpio);
  };

  const nueva = (): void => {
    setConversacionId(null);
    setVerHistorial(false);
    mut.reset();
  };

  const e = estado.data;
  const agotado = e !== undefined && e.preguntas_hoy >= e.max_preguntas_dia;
  const lista: Mensaje[] = conversacionId === null ? [] : (mensajes.data ?? []);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Asistente"
      subtitle="Consulta los datos del comité. Sólo lectura."
      icon={Sparkles}
      width="lg"
      footer={
        <form
          className="flex w-full items-end gap-2"
          onSubmit={(ev) => {
            ev.preventDefault();
            enviar(texto);
          }}
        >
          <textarea
            value={texto}
            onChange={(ev) => setTexto(ev.target.value)}
            onKeyDown={(ev) => {
              if (ev.key === "Enter" && !ev.shiftKey) {
                ev.preventDefault();
                enviar(texto);
              }
            }}
            rows={2}
            aria-label="Pregunta para el asistente"
            maxLength={2000}
            disabled={agotado}
            placeholder={agotado ? "Llegaste al máximo de preguntas de hoy." : "Escribe tu pregunta…"}
            className="flex-1 resize-none rounded-md border border-neutral-450 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-neutral-100"
          />
          <Button
            type="submit"
            icon={mut.isPending ? Loader2 : SendHorizonal}
            iconOnly
            aria-label="Enviar"
            disabled={mut.isPending || agotado || texto.trim().length < 2}
          />
        </form>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" icon={Plus} onClick={nueva}>
              Nueva
            </Button>
            <Button
              variant="ghost"
              size="sm"
              icon={History}
              onClick={() => setVerHistorial((v) => !v)}
            >
              Anteriores
            </Button>
          </div>
          {e && (
            <span className="text-xs text-neutral-500" title={`Modelo: ${e.modelo}`}>
              Hoy {e.preguntas_hoy}/{e.max_preguntas_dia} · Mes US$
              {Number(e.gasto_mes_usd).toFixed(2)} de {Number(e.tope_usd_mes).toFixed(0)}
            </span>
          )}
        </div>

        {verHistorial && conversaciones.isPending && <Cargando inline />}
        {verHistorial && conversaciones.isError && (
          <ErrorCarga
            error={conversaciones.error}
            onReintentar={() => void conversaciones.refetch()}
          />
        )}
        {verHistorial && conversaciones.data && (
          <ul className="divide-y divide-neutral-100 rounded-md border border-neutral-200 text-sm">
            {(conversaciones.data ?? []).length === 0 && (
              <li className="px-3 py-2 text-neutral-500">Sin conversaciones anteriores.</li>
            )}
            {(conversaciones.data ?? []).map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => {
                    setConversacionId(c.id);
                    setVerHistorial(false);
                  }}
                  className="w-full truncate px-3 py-2 text-left hover:bg-neutral-50"
                >
                  {c.titulo}
                </button>
              </li>
            ))}
          </ul>
        )}

        {lista.length === 0 && pendiente === null && (
          <div className="rounded-md bg-brand-50 p-4 text-sm text-neutral-700">
            <p className="mb-2 font-medium">Puedes preguntar, por ejemplo:</p>
            <div className="flex flex-col gap-1.5">
              {EJEMPLOS.map((ej) => (
                <button
                  key={ej}
                  type="button"
                  onClick={() => enviar(ej)}
                  disabled={agotado || mut.isPending}
                  className="text-left text-brand-700 hover:underline disabled:opacity-50"
                >
                  {ej}
                </button>
              ))}
            </div>
          </div>
        )}

        {conversacionId !== null && mensajes.isPending && <Cargando inline />}
        {mensajes.isError && (
          <ErrorCarga error={mensajes.error} onReintentar={() => void mensajes.refetch()} />
        )}
        {lista.map((m, i) => (
          <Burbuja key={i} mensaje={m} />
        ))}
        {pendiente !== null && (
          <>
            <Burbuja mensaje={{ rol: "user", texto: pendiente, herramientas: [], creado: "" }} />
            <div className="flex items-center gap-2 text-sm text-neutral-500">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Consultando los datos del comité…
            </div>
          </>
        )}
        {mut.isError && (
          <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {mut.error instanceof ApiError
              ? mut.error.message
              : "No se pudo consultar al asistente. Intenta de nuevo."}
          </p>
        )}
        <div ref={finRef} />

        <p className="text-xs leading-snug text-neutral-500">
          Respuestas generadas por IA a partir de los datos del comité. Verifica antes de actuar.
        </p>
      </div>
    </Drawer>
  );
}

function Burbuja({ mensaje }: { mensaje: Mensaje }): JSX.Element {
  if (mensaje.rol === "user") {
    return (
      <div className="ml-10 self-end rounded-lg bg-brand-700 px-3 py-2 text-sm text-white whitespace-pre-wrap">
        {mensaje.texto}
      </div>
    );
  }
  const usadas = [...new Set(mensaje.herramientas)];
  return (
    <div className="mr-6 flex flex-col gap-1">
      {usadas.length > 0 && (
        <p className="text-xs text-neutral-500">
          {usadas.map((h) => ETIQUETA_HERRAMIENTA[h] ?? h).join(" → ")}
        </p>
      )}
      <div className="rounded-lg bg-neutral-100 px-3 py-2 text-sm text-neutral-800">
        <TextoFormateado texto={mensaje.texto} />
      </div>
    </div>
  );
}

/** Formato mínimo sin HTML crudo: párrafos, viñetas "- " y **negrita**. */
function TextoFormateado({ texto }: { texto: string }): JSX.Element {
  const lineas = texto.split("\n");
  return (
    <div className="flex flex-col gap-1">
      {lineas.map((linea, i) => {
        const vineta = /^\s*[-•*]\s+/.test(linea);
        const contenido = vineta ? linea.replace(/^\s*[-•*]\s+/, "") : linea;
        if (contenido.trim() === "") return <div key={i} className="h-1" />;
        return (
          <p key={i} className={vineta ? "pl-4 -indent-3" : undefined}>
            {vineta && "• "}
            {conNegrita(contenido)}
          </p>
        );
      })}
    </div>
  );
}

function conNegrita(s: string): React.ReactNode[] {
  return s.split(/(\*\*[^*]+\*\*)/g).map((parte, i) =>
    parte.startsWith("**") && parte.endsWith("**") ? (
      <strong key={i}>{parte.slice(2, -2)}</strong>
    ) : (
      parte
    ),
  );
}
